import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import styled from 'styled-components';
import { PaymentInterfaceNew } from '../../../../new-lib/types/entity';
import {
  getPlanDuration,
  isCashPayment
} from '../../../../helpers/payment-plan';
import { useRouter } from 'next/router';
import PlanLoadModal from '../../components/payment-plans/mobile/PlanLoadModal';
import OriginalPlanIcon from '../../../../new-components/svg-icons/payment-plan/OriginalPlanIcon';
import CashPaymentIcon from '../../../../new-components/svg-icons/payment-plan/CashPaymentIcon';
import NawyNowIcon from '../../../../new-components/svg-icons/payment-plan/NawyNowIcon';

interface ChildProps {
  plan: PaymentInterfaceNew;
}
const CompoundInformativePlan = ({ plan }: ChildProps) => {
  const { t } = useTranslation(['entity', 'common']);

  const [showPlanDetails, setShowPlanDetails] = useState(false);

  const closePlanDetails = () => {
    setShowPlanDetails(false);
  };
  const { locale } = useRouter();

  const isCash = isCashPayment(
    plan.downPaymentPercentage,
    plan.installmentPercentage,
    plan.isCash
  );

  const getPlanIcon = () => {
    if (plan.nawyNow) {
      return <NawyNowIcon />;
    }

    if (isCash) {
      return <CashPaymentIcon />;
    }

    return <OriginalPlanIcon />;
  };

  const getPlanTitle = () => {
    return (
      <span className="plan-title-text">
        {isCash
          ? t('cash_payment')
          : plan.nawyNow
            ? 'Nawy Now'
            : t('original_plan')}
      </span>
    );
  };

  return (
    <InformativePlanContainer locale={locale} isNawyNow={plan.nawyNow}>
      <PlanLoadModal isOpen={showPlanDetails} closeFn={closePlanDetails} />
      <div className="plan-title">
        {getPlanIcon()}
        <span className="plan-type-text">{getPlanTitle()}</span>
      </div>
      <div className="details-section">
        <div className="down-payment">
          <span className="percentage">{`${plan.downPaymentPercentage}%`}</span>
          <span>{t('downpayment')}</span>
        </div>

        <div className="installment-years">
          <span className="years">
            {getPlanDuration({
              years: plan?.years
            })}
          </span>{' '}
          <span>{t('years')}</span>
        </div>
      </div>
      {plan.isBackLoaded && (
        <div className="plan-type">
          {t('escalating_payments', { ns: 'common' })}
        </div>
      )}
    </InformativePlanContainer>
  );
};

const InformativePlanContainer = styled.div<any>`
  width: 100%;
  flex: 1;
  min-width: 300px;
  max-width: 360px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  background: #fff;
  gap: 8px;
  border-radius: 12px;
  color: ${props => props.theme.light.colors.black};
  border: 0.5px solid #edeff3;
  position: relative;
  .plan-type {
    position: absolute;
    top: 12px;
    inset-inline-end: 12px;
    font-family: materialMedium;
    font-size: 11px;
    color: #3e4042;
    display: flex;
    align-items: center;
    border-radius: 100px;
    background: #f2f6fd;
    padding: 4px 8px;
  }

  .plan-title {
    display: flex;
    align-items: center;
    gap: 4px;
    svg {
      width: 18px;
      height: 18px;
      fill: #015c9a;
    }

    .plan-type-text {
      color: ${props => (props.isNawyNow ? '#FF5E00' : '#015c9a')};
      font-family: materialMedium;
      font-size: 16px;
    }
  }

  .details-section {
    width: 100%;
    display: flex;
    gap: 12px;
    align-items: flex-end;
    justify-content: space-between;
  }

  .down-payment {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    font-size: 14px;

    span {
      &:first-child {
        color: #3e4042;
        font-family: materialBold;
        font-size: 28px;
      }

      &:last-child {
        color: #2c3138;
        font-family: materialRegular;
        font-size: 14px;
        margin-bottom: 4px;
      }
    }
  }

  .installment-years {
    color: #3e4042;
    font-family: materialSemiBold;
    font-size: 16px;
    ${props => props.locale == 'ar' && 'align-self: center'};
  }

  .escalating-section {
    display: flex;
    gap: 12px;
    min-width: 40%;
    max-width: 40%;
  }

  .separator {
    width: 0.5px;
    height: 33px;
    background: #d3dbe6;
  }

  .plan-info-alert {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 16px;
    gap: 16px;

    .plan-info {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .plan-info-title {
        font-size: ${props => props.theme.font.sizes.m};
        font-family: materialBold;
      }

      .plan-info-description {
        font-size: ${props => props.theme.font.sizes.xxs};
        font-family: materialMedium;
      }
    }

    button {
      min-width: 55px;
    }
  }

  @media screen and (max-width: 768px) {
    .plan-title {
      svg {
        width: 16px;
        height: 16px;
      }

      .plan-type-text {
        font-size: 16px;
      }
    }
    .down-payment {
      span {
        &:first-child {
          font-size: 22px;
          line-height: 22px;
        }

        &:last-child {
          font-size: 12px;
          margin-bottom: 0;
        }
      }
    }
    .installment-years {
      font-size: 14px;
    }
  }
`;

export default CompoundInformativePlan;
